import * as React from 'react'

import { cn } from '@/lib/utils'

interface MonthInputProps
  extends Omit<React.ComponentProps<'input'>, 'type' | 'value' | 'onChange'> {
  value: string
  onValueChange?: (value: string) => void
}

const MONTH_OPTIONS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

function formatYearMonth(year: number, month: number) {
  return `${year}-${String(month).padStart(2, '0')}`
}

function parseYearMonth(value: string) {
  const [yearString, monthString] = value.split('-')
  const year = Number(yearString)
  const month = Number(monthString)
  return {
    year: Number.isInteger(year) ? year : new Date().getFullYear(),
    month: month >= 1 && month <= 12 ? month : 1,
  }
}

function isSafariBrowser() {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.userAgent !== 'string'
  ) {
    return false
  }
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
}

function MonthInput({
  className,
  value,
  onValueChange,
  id,
  name,
  disabled,
  ...props
}: MonthInputProps) {
  const [supportsMonthInput, setSupportsMonthInput] = React.useState(false)

  React.useEffect(() => {
    const input = document.createElement('input')
    input.setAttribute('type', 'month')
    input.value = '2000-01'

    const isSupported = input.type === 'month' && input.value === '2000-01'
    setSupportsMonthInput(isSupported && !isSafariBrowser())
  }, [])

  const { year, month } = parseYearMonth(value)

  const handleValueChange = (nextValue: string) => {
    onValueChange?.(nextValue)
  }

  if (supportsMonthInput) {
    return (
      <input
        id={id}
        name={name}
        disabled={disabled}
        type="month"
        value={value}
        onChange={(event) => handleValueChange(event.target.value)}
        onInput={(event) => handleValueChange(event.currentTarget.value)}
        className={cn(
          'flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs outline-none transition-[color,box-shadow] selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:font-medium file:text-foreground file:text-sm placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30',
          'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
          'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
          className,
        )}
        {...props}
      />
    )
  }

  const currentYear = new Date().getFullYear()
  const years = Array.from(
    { length: 11 },
    (_, index) => currentYear - 5 + index,
  )

  return (
    <div
      className={cn(
        'flex h-9 items-center gap-2 rounded-md border border-input bg-transparent px-2 text-sm shadow-xs',
        className,
      )}
      {...props}
    >
      <select
        id={id}
        name={name}
        disabled={disabled}
        value={month}
        onChange={(event) =>
          handleValueChange(formatYearMonth(year, Number(event.target.value)))
        }
        className="flex-1 appearance-none bg-transparent text-base outline-none"
      >
        {MONTH_OPTIONS.map((label, index) => (
          <option key={label} value={index + 1}>
            {label}
          </option>
        ))}
      </select>
      <span className="text-muted-foreground text-sm">•</span>
      <select
        disabled={disabled}
        value={year}
        onChange={(event) =>
          handleValueChange(formatYearMonth(Number(event.target.value), month))
        }
        className="flex-1 appearance-none bg-transparent text-base outline-none"
      >
        {years.map((yearOption) => (
          <option key={yearOption} value={yearOption}>
            {yearOption}
          </option>
        ))}
      </select>
    </div>
  )
}

export { MonthInput }
